import { IPMEventResponse, IPMEventPayload } from '@bg-shared/integrations/shared/interfaces';
import { WINDOW_NAME } from '@bg-shared/integrations/shared/constants';
import { PubSub } from '@bg-shared/utils/PubSub';

export interface IPostMessageApi {
    emit(message: IPMEventPayload): void;
    subscribe<T = unknown>(type: string, listener: ICallback<IPMEventResponse<T>>): ICallback;
}

export const createPostMessenger = (target: Window): IPostMessageApi => {
    const listeners = new PubSub<IPMEventResponse>();

    const validateResponse = (event: MessageEvent): IPMEventPayload | boolean => {
        if (!listeners.hasTypes() || typeof event.data !== 'string') {
            return false;
        }

        try {
            const parsed = JSON.parse(event.data);

            if (!(parsed.windowName as string)?.includes(WINDOW_NAME)) {
                return false;
            }

            return parsed;
        } catch (error) {
            return false;
        }
    };

    const validateRequest = (type: string, payload?: unknown): string | boolean => {
        try {
            return JSON.stringify({
                type,
                payload,
            });
        } catch (error) {
            return false;
        }
    };

    window.addEventListener(
        'message',
        (event: MessageEvent) => {
            const validatedMessage = validateResponse(event);

            if (!validatedMessage) {
                return;
            }

            const message = validatedMessage as IPMEventPayload;

            listeners.publish(message, message.type);
        },
        false,
    );

    return {
        emit: (message: IPMEventPayload): void => {
            const validated = validateRequest(message.type, message.payload);

            if (!validated) {
                return;
            }

            target.postMessage(validated, '*');
        },
        subscribe: <T = unknown>(
            type: string,
            listener: ICallback<IPMEventResponse<T>>,
        ): ICallback => {
            return listeners.subscribe<IPMEventResponse<T>>(listener, type);
        },
    };
};
